import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", phone: "", mobile: "", fax: "", email: "", address: "", notes: "" };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setRows((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(editId ? "Customer updated" : "Customer added");
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const edit = (c) => { setEditId(c.id); setForm({ name: c.name, phone: c.phone || "", mobile: c.mobile || "", fax: c.fax || "", email: c.email || "", address: c.address || "", notes: c.notes || "" }); setOpen(true); };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load();
  };

  const filtered = rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Homeowners, property managers and repeat clients.</p>
        </div>
        <Button data-testid="add-customer-btn" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setEditId(null); setForm(empty); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-1.5" /> Add Customer
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input data-testid="customers-search" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 h-8" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Property Address</th>
                <th className="text-left px-5 py-3">Notes</th>
                <th className="text-right px-5 py-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No customers yet.</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="odd:bg-slate-50/50 hover:bg-slate-50 border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <Link to={`/dashboard/customers/${c.id}`} className="hover:text-blue-600 inline-flex items-center gap-1" data-testid={`open-customer-${c.id}`}>
                      {c.name}
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.phone || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.address || "—"}</td>
                  <td className="px-5 py-3 text-slate-600 max-w-[240px] truncate">{c.notes || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => edit(c)} className="p-1.5 hover:bg-slate-100 rounded"><Pencil className="w-4 h-4 text-slate-600" /></button>
                    <button data-testid={`delete-customer-${c.id}`} onClick={() => del(c)} className="p-1.5 hover:bg-slate-100 rounded ml-1"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                <Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mobile</Label>
                <Input data-testid="customer-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                <Input data-testid="customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Fax</Label>
                <Input data-testid="customer-fax" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Property Address</Label>
              <Input data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Job Notes</Label>
              <Textarea data-testid="customer-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="customer-save" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
